import { useEffect, useState } from "react";
import { PageHeader } from "../../../components/PageHeader/index.js";
import {
  quarantineService,
  type QuarantineCategory,
  type QuarantineFinding,
  type ListQuarantineResponse,
} from "../../../api/quarantine.service.js";
import styles from "./PromoteImpact.module.css";

// ── Category config ──────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<QuarantineCategory, string> = {
  "page-loss": "Drops off firm list",
  "photo/bio": "Profiles losing photo/bio",
  "firm-loss": "Firms / offices lost",
  "re-key": "URLs moved",
};

const ALL_CATEGORIES: QuarantineCategory[] = ["page-loss", "photo/bio", "firm-loss", "re-key"];

// ── Sub-components ───────────────────────────────────────────────────────────

type StatusValue = QuarantineFinding["status"];

function StatusBadge({ status }: { status: StatusValue }) {
  const cls =
    status === "needs_redirect"
      ? styles.badgeRedirect
      : status === "resolved"
        ? styles.badgeResolved
        : styles.badgeOpen;

  const label =
    status === "needs_redirect" ? "Needs redirect" : status === "resolved" ? "Resolved" : "Open";

  return <span className={`${styles.badge} ${cls}`}>{label}</span>;
}

function CategoryTag({ category }: { category: QuarantineCategory }) {
  const cls =
    category === "page-loss"
      ? styles.tagPageLoss
      : category === "photo/bio"
        ? styles.tagPhotoBio
        : category === "firm-loss"
          ? styles.tagFirmLoss
          : styles.tagReKey;

  return <span className={`${styles.typeTag} ${cls}`}>{CATEGORY_LABELS[category]}</span>;
}

function RowDetail({ row }: { row: QuarantineFinding }) {
  const isRekey = row.category === "re-key";
  const urls = row.urls as Record<string, string>;

  const detailBorderClass =
    row.status === "needs_redirect"
      ? styles.detailInnerRedirect
      : row.status === "resolved"
        ? styles.detailInnerResolved
        : styles.detailInnerOpen;

  return (
    <tr className={styles.detailRow}>
      <td colSpan={5}>
        <div
          className={`${styles.detailInner} ${detailBorderClass}`}
          data-testid={`qdetail-${row.source_id}`}
        >
          <dl className={styles.detailGrid}>
            {isRekey ? (
              <>
                <dt className={styles.detailKey}>URL</dt>
                <dd className={styles.detailValue}>
                  <div className={styles.rekeyBlock}>
                    <span className={styles.rekeyOld}>{urls.old_url}</span>
                    <span className={styles.rekeyArrow}>→</span>
                    <span className={`${styles.rekeyNew} ${styles.mono}`}>{urls.new_url}</span>
                  </div>
                </dd>

                {row.match_basis && (
                  <>
                    <dt className={styles.detailKey}>Match</dt>
                    <dd className={`${styles.detailValue} ${styles.matchedHint}`}>
                      {row.match_basis}
                    </dd>
                  </>
                )}

                <dt className={styles.detailKey}>Why</dt>
                <dd className={styles.detailValue}>{row.why}</dd>

                <dt className={styles.detailKey}>Fix</dt>
                <dd className={styles.detailValue}>
                  {row.fix}
                  <div className={styles.verifyNote}>
                    Verify the new URL is live before adding the redirect.
                  </div>
                </dd>
              </>
            ) : (
              <>
                <dt className={styles.detailKey}>URL</dt>
                <dd className={`${styles.detailValue} ${styles.mono}`}>{urls.url}</dd>

                <dt className={styles.detailKey}>Why</dt>
                <dd className={styles.detailValue}>{row.why}</dd>

                <dt className={styles.detailKey}>Fix</dt>
                <dd className={styles.detailValue}>{row.fix}</dd>
              </>
            )}
          </dl>
        </div>
      </td>
    </tr>
  );
}

type SortKey = "name" | "firm" | "category" | "status";
type SortDir = "asc" | "desc";

function sortFindings(rows: QuarantineFinding[], key: SortKey, dir: SortDir): QuarantineFinding[] {
  return [...rows].sort((a, b) => {
    let av = "";
    let bv = "";
    if (key === "name") {
      av = a.name;
      bv = b.name;
    } else if (key === "firm") {
      av = a.firm ?? "";
      bv = b.firm ?? "";
    } else if (key === "category") {
      av = a.category;
      bv = b.category;
    } else if (key === "status") {
      av = a.status;
      bv = b.status;
    }
    const cmp = av.localeCompare(bv);
    return dir === "asc" ? cmp : -cmp;
  });
}

function QuarantineTable({
  rows,
  sortKey,
  sortDir,
  onSort,
}: {
  rows: QuarantineFinding[];
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  function toggleRow(id: string) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function sortIndicator(key: SortKey) {
    if (sortKey !== key) return <span className={styles.sortNeutral}>↕</span>;
    return <span className={styles.sortActive}>{sortDir === "asc" ? "↑" : "↓"}</span>;
  }

  if (rows.length === 0) {
    return <div className={styles.emptyState}>No rows match the current filter.</div>;
  }

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th>
            <button type="button" className={styles.sortBtn} onClick={() => onSort("name")}>
              Name {sortIndicator("name")}
            </button>
          </th>
          <th>
            <button type="button" className={styles.sortBtn} onClick={() => onSort("firm")}>
              Firm {sortIndicator("firm")}
            </button>
          </th>
          <th>
            <button type="button" className={styles.sortBtn} onClick={() => onSort("category")}>
              Type {sortIndicator("category")}
            </button>
          </th>
          <th>
            <button type="button" className={styles.sortBtn} onClick={() => onSort("status")}>
              Status {sortIndicator("status")}
            </button>
          </th>
          <th className={styles.chevCol} aria-label="Expand" />
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const isExpanded = expandedIds.has(row.source_id);
          return [
            <tr
              key={`row-${row.source_id}`}
              className={`${styles.tableRow}${isExpanded ? ` ${styles.expandedRow}` : ""}`}
              data-testid={`qrow-${row.source_id}`}
              onClick={() => toggleRow(row.source_id)}
            >
              <td className={styles.nameCell}>{row.name || row.source_id}</td>
              <td className={styles.firmCell}>{row.firm ?? "—"}</td>
              <td>
                <CategoryTag category={row.category} />
              </td>
              <td>
                <StatusBadge status={row.status} />
              </td>
              <td className={styles.chevCol}>
                <i className={`${styles.chevron}${isExpanded ? ` ${styles.chevronOpen}` : ""}`}>
                  ›
                </i>
              </td>
            </tr>,
            isExpanded ? <RowDetail key={`detail-${row.source_id}`} row={row} /> : null,
          ];
        })}
      </tbody>
    </table>
  );
}

// ── Counter cards ─────────────────────────────────────────────────────────────

function DeltaIndicator({ delta }: { delta: number }) {
  if (delta === 0) return null;
  return (
    <span className={delta > 0 ? styles.deltaUp : styles.deltaDown}>
      {delta > 0 ? `+${delta}` : String(delta)}
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PromoteImpactPage() {
  const [response, setResponse] = useState<ListQuarantineResponse | null>(null);
  const [activeCategory, setActiveCategory] = useState<QuarantineCategory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | StatusValue>("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [offset, setOffset] = useState(0);

  const PAGE_SIZE = 50;

  async function fetchPage(category: QuarantineCategory | null, newOffset: number) {
    try {
      const res = await quarantineService.listQuarantine(
        category
          ? { category, limit: PAGE_SIZE, offset: newOffset }
          : { limit: PAGE_SIZE, offset: newOffset },
      );
      setResponse(res);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    fetchPage(null, 0);
  }, []);

  function handleCategoryClick(category: QuarantineCategory) {
    const next = activeCategory === category ? null : category;
    setActiveCategory(next);
    setOffset(0);
    fetchPage(next, 0);
  }

  function handleAllClick() {
    setActiveCategory(null);
    setOffset(0);
    fetchPage(null, 0);
  }

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function handlePrev() {
    const newOffset = Math.max(0, offset - PAGE_SIZE);
    setOffset(newOffset);
    fetchPage(activeCategory, newOffset);
  }

  function handleNext() {
    if (!response?.pagination.has_more) return;
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    fetchPage(activeCategory, newOffset);
  }

  const counts = response?.counts;
  const delta = response?.delta ?? ({} as Record<QuarantineCategory, number>);
  const rawRows = response?.rows ?? [];
  const allCount = response?.all ?? 0;
  const pagination = response?.pagination;

  const filteredRows = rawRows.filter(row => {
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const nameMatch = row.name.toLowerCase().includes(q);
      const firmMatch = (row.firm ?? "").toLowerCase().includes(q);
      const urlStr = "url" in row.urls ? row.urls.url : `${row.urls.old_url} ${row.urls.new_url}`;
      const urlMatch = urlStr.toLowerCase().includes(q);
      if (!nameMatch && !firmMatch && !urlMatch) return false;
    }
    if (statusFilter) {
      if (row.status !== statusFilter) return false;
    }
    return true;
  });

  const sortedRows = sortFindings(filteredRows, sortKey, sortDir);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Promote Impact"
        subtitle="Pre-flip window: fix in WordPress, watch these drain to zero."
      />

      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* Counter cards */}
      <div className={styles.cards}>
        <button
          type="button"
          className={`${styles.card}${activeCategory === null ? ` ${styles.cardActive}` : ""}`}
          data-testid="chip-all"
          onClick={handleAllClick}
        >
          <span className={styles.cardLabel}>All impacted</span>
          <div className={styles.cardCount}>
            <span className={styles.cardNum}>{allCount}</span>
          </div>
        </button>

        {ALL_CATEGORIES.map(cat => {
          const count = counts?.[cat];
          const d = delta[cat] ?? 0;
          return (
            <button
              key={cat}
              type="button"
              className={`${styles.card}${activeCategory === cat ? ` ${styles.cardActive}` : ""}`}
              data-testid={`chip-${cat}`}
              onClick={() => handleCategoryClick(cat)}
            >
              <span className={styles.cardLabel}>{CATEGORY_LABELS[cat]}</span>
              <div className={styles.cardCount}>
                <span className={styles.cardNum}>{count?.open ?? 0}</span>
                <DeltaIndicator delta={d} />
                {count != null && count.resolved > 0 && (
                  <span className={styles.cardResolved}>{count.resolved} resolved</span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles.searchInput}
          placeholder="Search by name, firm or URL…"
          data-testid="quarantine-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search quarantine rows"
        />
        <select
          className={styles.dispositionSelect}
          data-testid="status-filter"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as "" | StatusValue)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="needs_redirect">Needs redirect</option>
          <option value="resolved">Resolved</option>
        </select>
        <span className={styles.rowCount}>{filteredRows.length} rows</span>
      </div>

      {/* Table */}
      <div className={styles.mainSection}>
        <div className={styles.tableContainer}>
          <QuarantineTable
            rows={sortedRows}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
          />
        </div>

        {/* Pagination */}
        {pagination && (pagination.has_more || offset > 0) && (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={offset === 0}
              onClick={handlePrev}
            >
              ← Prev
            </button>
            <span className={styles.pageInfo}>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, pagination.total)} of {pagination.total}
            </span>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={!pagination.has_more}
              onClick={handleNext}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
